/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';

import Loader from 'components/atoms/loader';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import {
  apenasDigitos,
  prazoAcima,
  useProposalConditions,
  validadeAbaixo,
} from 'hooks/useProposalConditions';
import { CompanyCustomField } from 'services/actions/ai_management_proposal_company';
import { proposalFieldTarget } from 'services/ai_management_proposal';
import { Container } from './styles';

interface AiManagementProposalConditionsProps {
  biddingId: number;
  onPendencies: (count: number) => void;
  // O que falta, em texto, para a caixa "Para concluir esta etapa".
  onPendencyItems?: (itens: string[]) => void;
}

// Mesma regra do back: nome obrigatório e sem repetir (ignorando maiúsculas e espaços).
const nomeInvalido = (nome: string, campos: CompanyCustomField[], ignorarId?: string) => {
  const limpo = nome.trim();
  if (!limpo) return 'Informe o nome do campo.';
  const repetido = campos.some(
    (c) => c.id !== ignorarId && c.nome.trim().toLowerCase() === limpo.toLowerCase(),
  );
  return repetido ? 'Já existe um campo com esse nome.' : '';
};

// Valem só para esta proposta e são conferidas contra as regras confirmadas (validade mínima e
// prazo de entrega máximo).
const AiManagementProposalConditions: React.FC<AiManagementProposalConditionsProps> = ({
  biddingId,
  onPendencies,
  onPendencyItems,
}) => {
  const { trackSave } = useProposalShell();
  const { draft, limits, loading, loadError, saveError, pendencies, update, reload } =
    useProposalConditions(biddingId, { trackSave });

  const [adding, setAdding] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; nome: string } | null>(null);
  const [undoField, setUndoField] = useState<CompanyCustomField | null>(null);
  const [menuCampo, setMenuCampo] = useState<string | null>(null);

  useEffect(() => {
    onPendencies(pendencies);
  }, [pendencies, onPendencies]);

  useEffect(() => {
    if (!onPendencyItems || !draft) return;
    const { validadeMinima, prazoMaximo } = limits;
    const itens: string[] = [];
    if (
      validadeMinima !== null &&
      (!draft.validade_dias.trim() || validadeAbaixo(draft.validade_dias, validadeMinima))
    ) {
      itens.push(`A proposta deve permanecer válida por pelo menos ${validadeMinima} dias.`);
    }
    if (
      prazoMaximo !== null &&
      (!draft.prazo_entrega_dias.trim() || prazoAcima(draft.prazo_entrega_dias, prazoMaximo))
    ) {
      itens.push(`O prazo de entrega deve ser de no máximo ${prazoMaximo} dias.`);
    }
    onPendencyItems(itens);
  }, [draft, limits, onPendencyItems]);

  if (loadError) {
    return (
      <div className="alert alert-warning d-flex align-items-center justify-content-between mt-4">
        <span>Não foi possível carregar as condições da proposta.</span>
        <button type="button" className="btn btn-sm btn-link p-0" onClick={reload}>
          Tentar de novo
        </button>
      </div>
    );
  }
  if (loading || !draft) return <Loader />;

  const campos = draft.campos_personalizados;
  const { validadeMinima, prazoMaximo } = limits;
  const validadeErro = validadeAbaixo(draft.validade_dias, validadeMinima);
  const prazoErro = prazoAcima(draft.prazo_entrega_dias, prazoMaximo);
  const erroDoBack = (field: string) => (saveError?.field === field ? saveError.message : '');

  const novoNomeErro = novoNome.trim() ? nomeInvalido(novoNome, campos) : '';
  const renameErro = renaming ? nomeInvalido(renaming.nome, campos, renaming.id) : '';

  const addField = () => {
    if (nomeInvalido(novoNome, campos)) return;
    update({
      campos_personalizados: [
        ...campos,
        { id: `cp_${Date.now()}`, nome: novoNome.trim(), valor: '', ordem: campos.length + 1 },
      ],
    });
    setNovoNome('');
    setAdding(false);
  };

  const setValor = (id: string, valor: string) =>
    update({ campos_personalizados: campos.map((c) => (c.id === id ? { ...c, valor } : c)) });

  const confirmRename = () => {
    if (!renaming || renameErro) return;
    const nome = renaming.nome.trim();
    update({
      campos_personalizados: campos.map((c) => (c.id === renaming.id ? { ...c, nome } : c)),
    });
    setRenaming(null);
  };

  const removeField = (campo: CompanyCustomField) => {
    update({ campos_personalizados: campos.filter((c) => c.id !== campo.id) });
    setUndoField(campo);
  };

  const undoRemove = () => {
    if (undoField && !nomeInvalido(undoField.nome, campos)) {
      update({ campos_personalizados: [...campos, undoField] });
    }
    setUndoField(null);
  };

  const renderDias = (
    chave: 'validade_dias' | 'prazo_entrega_dias',
    rotulo: string,
    limite: number | null,
    foraDaRegra: boolean,
    ajuda: string,
    erro: string,
  ) => {
    const erroBack = erroDoBack(chave);
    return (
      <label
        className={`proposal-conditions-field d-block mb-3 ${foraDaRegra ? 'invalid' : ''}`}
        htmlFor={`condicao-${chave}`}
        data-proposal-target={proposalFieldTarget(chave)}
      >
        <span className="small d-block">{rotulo}</span>
        <span className="proposal-conditions-suffix">
          <input
            id={`condicao-${chave}`}
            type="text"
            inputMode="numeric"
            className="form-control form-control-sm"
            aria-describedby={`condicao-${chave}-ajuda`}
            value={draft[chave]}
            onChange={(event) => {
              const { value } = event.target;
              update({ [chave]: apenasDigitos(value) });
            }}
          />
          <span>dias</span>
        </span>
        {limite !== null && (
          <small
            id={`condicao-${chave}-ajuda`}
            className={`proposal-conditions-help ${foraDaRegra ? 'erro' : ''}`}
          >
            <i className="material-icons-outlined" aria-hidden="true">
              {foraDaRegra ? 'error_outline' : 'smart_toy'}
            </i>
            {foraDaRegra ? erro : ajuda}
          </small>
        )}
        {erroBack && <small className="proposal-conditions-help erro">{erroBack}</small>}
      </label>
    );
  };

  return (
    <Container className="proposal-conditions form-card">
      <div className="section-head">
        <h2>
          <i className="material-icons-outlined" aria-hidden="true">
            assignment
          </i>
          Condições da proposta
        </h2>
      </div>
      <div className="conditions-grid">
        {renderDias(
          'validade_dias',
          'Validade da proposta',
          validadeMinima,
          validadeErro,
          `Mínimo exigido pelo edital: ${validadeMinima} dias`,
          `O edital exige validade mínima de ${validadeMinima} dias.`,
        )}
        {renderDias(
          'prazo_entrega_dias',
          'Prazo de entrega',
          prazoMaximo,
          prazoErro,
          `Máximo exigido pelo edital: ${prazoMaximo} dias`,
          `O edital permite prazo de entrega de no máximo ${prazoMaximo} dias.`,
        )}

        <label className="proposal-conditions-field wide d-block mb-3" htmlFor="condicao-pagamento">
          <span className="small d-block">Condições de pagamento</span>
          <textarea
            id="condicao-pagamento"
            className="form-control form-control-sm"
            rows={2}
            value={draft.condicoes_pagamento}
            onChange={(event) => {
              const { value } = event.target;
              update({ condicoes_pagamento: value });
            }}
          />
        </label>

        <label className="proposal-conditions-field d-block mb-3" htmlFor="condicao-garantia">
          <span className="small d-block">Garantia</span>
          <input
            id="condicao-garantia"
            type="text"
            className="form-control form-control-sm"
            value={draft.garantia}
            onChange={(event) => {
              const { value } = event.target;
              update({ garantia: value });
            }}
          />
        </label>

        {campos.map((c) =>
          renaming?.id === c.id ? (
            <div key={c.id} className="proposal-conditions-field mb-3">
              <label className="d-block mb-1" htmlFor={`condicao-cp-nome-${c.id}`}>
                <span className="small d-block">Nome do campo</span>
                <input
                  id={`condicao-cp-nome-${c.id}`}
                  type="text"
                  className="form-control form-control-sm"
                  value={renaming.nome}
                  onChange={(event) => {
                    const { value } = event.target;
                    setRenaming({ id: c.id, nome: value });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') confirmRename();
                    if (event.key === 'Escape') setRenaming(null);
                  }}
                />
              </label>
              {renameErro && <small className="proposal-conditions-help erro">{renameErro}</small>}
              <button
                type="button"
                className="btn btn-sm btn-primary mr-2 mt-1"
                disabled={!!renameErro}
                onClick={confirmRename}
              >
                Salvar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link mt-1"
                onClick={() => setRenaming(null)}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <div key={c.id} className="proposal-conditions-field mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <label className="field-label mb-0" htmlFor={`condicao-cp-${c.id}`}>
                  {c.nome}
                </label>
                <span className="field-label-actions">
                  <button
                    type="button"
                    aria-label={`Opções do campo ${c.nome}`}
                    aria-expanded={menuCampo === c.id}
                    onClick={() => setMenuCampo((atual) => (atual === c.id ? null : c.id))}
                  >
                    <i className="material-icons-outlined" aria-hidden="true">
                      more_vert
                    </i>
                  </button>
                  {menuCampo === c.id && (
                    <span className="field-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuCampo(null);
                          setRenaming({ id: c.id, nome: c.nome });
                        }}
                      >
                        <i className="material-icons-outlined" aria-hidden="true">
                          edit
                        </i>
                        Renomear
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="text-danger"
                        onClick={() => {
                          setMenuCampo(null);
                          removeField(c);
                        }}
                      >
                        <i className="material-icons-outlined" aria-hidden="true">
                          delete
                        </i>
                        Excluir
                      </button>
                    </span>
                  )}
                </span>
              </div>
              <input
                id={`condicao-cp-${c.id}`}
                type="text"
                className="form-control form-control-sm"
                value={c.valor}
                onChange={(event) => {
                  const { value } = event.target;
                  setValor(c.id, value);
                }}
              />
            </div>
          ),
        )}
      </div>

      {erroDoBack('campos_personalizados') && (
        <div className="alert alert-danger py-2">{erroDoBack('campos_personalizados')}</div>
      )}

      <button
        type="button"
        className="btn btn-sm btn-link p-0 mt-3 d-inline-flex align-items-center"
        onClick={() => setAdding(!adding)}
      >
        <i className="material-icons-outlined" aria-hidden="true" style={{ fontSize: 18 }}>
          add
        </i>
        Campo
      </button>
      {adding && (
        <div className="proposal-conditions-popover mt-2">
          <strong className="d-block mb-2">Novo campo</strong>
          <label className="d-block mb-1" htmlFor="condicao-novo-campo">
            <span className="small d-block">Nome do campo</span>
            <input
              id="condicao-novo-campo"
              type="text"
              className="form-control form-control-sm"
              value={novoNome}
              onChange={(event) => {
                const { value } = event.target;
                setNovoNome(value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addField();
              }}
            />
          </label>
          {novoNomeErro && <small className="proposal-conditions-help erro">{novoNomeErro}</small>}
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              onClick={() => {
                setAdding(false);
                setNovoNome('');
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!novoNome.trim() || !!novoNomeErro}
              onClick={addField}
            >
              Adicionar
            </button>
          </div>
        </div>
      )}

      {undoField && (
        <div className="alert alert-dark d-flex align-items-center justify-content-between py-2 mt-3">
          <span>Campo removido.</span>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={undoRemove}>
            Desfazer
          </button>
        </div>
      )}
    </Container>
  );
};

export default AiManagementProposalConditions;
